import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Linkify } from "@/components/Linkify";
import { FormFields } from "@/components/public/FormFields";
import { ClipboardList, HeartPulse, Smartphone } from "lucide-react";

const logoSrc = (b) => (b?.logo_url ? (b.logo_url.startsWith("/") ? process.env.REACT_APP_BACKEND_URL + b.logo_url : b.logo_url) : null);

// Phone-sized frame that shows content the way the client will see it on their device.
const Frame = ({ open, onClose, children, label }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="client-preview">
      <DialogHeader className="px-5 pt-5 pb-3 border-b">
        <DialogTitle className="font-heading flex items-center gap-2 text-base"><Smartphone className="h-4 w-4 text-primary" />{label}</DialogTitle>
        <p className="text-xs text-muted-foreground">This is what your client sees when they open the link.</p>
      </DialogHeader>
      <div className="bg-muted/40 max-h-[70vh] overflow-y-auto p-4">{children}</div>
    </DialogContent>
  </Dialog>
);

export const FormPreview = ({ template, onClose }) => {
  const { business } = useAuth();
  const [answers, setAnswers] = useState({});
  const set = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));
  const logo = logoSrc(business);
  return (
    <Frame open={!!template} onClose={onClose} label="Form preview">
      {template && (
        <>
          {logo && <img src={logo} alt="" className="h-10 mx-auto mb-4 object-contain" />}
          <div className="rounded-2xl border bg-card p-5 shadow-sm space-y-5" data-testid="form-preview-card">
            <h1 className="font-heading font-extrabold text-2xl">{template.name || "Untitled form"}</h1>
            {!(template.elements || []).length && <p className="text-sm text-muted-foreground">No fields yet — add some elements to see them here.</p>}
            <FormFields elements={template.elements} answers={answers} set={set} />
            <Button className="rounded-lg w-full" disabled data-testid="form-preview-submit">Submit form</Button>
          </div>
        </>
      )}
    </Frame>
  );
};

export const InstructionsPreview = ({ item, kind = "instructions", onClose }) => {
  const { business } = useAuth();
  const logo = logoSrc(business);
  const Icon = kind === "aftercare" ? HeartPulse : ClipboardList;
  return (
    <Frame open={!!item} onClose={onClose} label={kind === "aftercare" ? "Aftercare preview" : "Instructions preview"}>
      {item && (
        <>
          <div className="flex items-center gap-3 mb-5">
            {logo ? <img src={logo} alt="" className="h-11 w-11 rounded-xl object-cover border border-border" /> : <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center"><Icon className="h-5 w-5 text-primary" /></div>}
            <div>
              <div className="font-heading font-bold">{business?.name || "Your clinic"}</div>
              <div className="text-[11px] text-muted-foreground uppercase tracking-widest font-mono-label">{kind === "aftercare" ? "Aftercare instructions" : business?.phone || ""}</div>
            </div>
          </div>
          <div className="rounded-2xl border bg-card p-5 shadow-sm" data-testid="instructions-preview-card">
            {kind !== "aftercare" && <div className="font-mono-label text-[11px] uppercase tracking-widest text-primary mb-2">{item.service_name || "Treatment"}</div>}
            <h1 className="font-heading font-extrabold text-2xl tracking-tight" data-testid="instructions-preview-title">{item.title || "Untitled"}</h1>
            <p className="text-muted-foreground text-sm mt-1">Prepared for {item.client_name || "Jane"}</p>
            <div className="mt-5 whitespace-pre-wrap leading-relaxed text-[15px]" data-testid="instructions-preview-body">
              {item.instructions?.trim() ? <Linkify text={item.instructions} /> : <span className="text-muted-foreground">Nothing written yet.</span>}
            </div>
          </div>
          <p className="text-center text-xs text-muted-foreground mt-5">If you have any concerns, please contact {business?.name || "your clinic"}{business?.phone ? ` on ${business.phone}` : ""}.</p>
        </>
      )}
    </Frame>
  );
};
